import type { Account, DashboardData, IOU, Transaction } from "../../types";
import { isCard, reportableBase } from "../../lib/finance";

// What the overview shows, worked out from the ledger rather than stored anywhere. Pure functions
// of the data and the selected month, so each one can be read — and tested — on its own, instead
// of being a block in the middle of a render.

/** Cash, card debt and the difference: the household's position right now. */
export function position(accounts: Account[]) {
  const sum = (list: Account[]) =>
    list.some((a) => a.balanceBase === null) ? null : list.reduce((s, a) => s + (a.balanceBase ?? 0), 0);
  const cash = sum(accounts.filter((a) => !isCard(a)));
  const debt = sum(accounts.filter(isCard));
  return {
    cash,
    debt,
    net: cash === null || debt === null ? null : cash - debt,
    byCurrency: (currency: string) =>
      accounts.filter((a) => !isCard(a) && a.currency === currency).reduce((s, a) => s + a.balance, 0),
  };
}

export interface OwedLine {
  key: string;
  dot: "mint" | "lavender";
  label: string;
  amount: number;
  currency: string;
}

/**
 * Open loans, scoped to the same accounts as the position above them so the two always describe
 * the same pot of money. A loan logged before funding accounts were recorded cannot be attributed
 * to one, so it is always included rather than silently dropped.
 */
export function owed(ious: IOU[], accounts: Account[]) {
  const open = ious.filter(
    (i) => i.status === "Open" && (!i.fundingAccountId || accounts.some((a) => a.id === i.fundingAccountId)),
  );
  const lines: OwedLine[] = Array.from(new Set(open.map((i) => i.currency)))
    .sort()
    .flatMap((currency) => {
      const total = (direction: string) =>
        open
          .filter((i) => i.currency === currency && i.direction === direction)
          .reduce((sum, i) => sum + i.outstanding, 0);
      // Mint and lavender carry the same meaning as in the position: an asset and a liability.
      return [
        { key: `${currency}-in`, dot: "mint" as const, label: `Owed to you (${currency})`, amount: total("Owed to us"), currency },
        { key: `${currency}-out`, dot: "lavender" as const, label: `You owe (${currency})`, amount: total("We owe"), currency },
      ].filter((line) => line.amount > 0.005);
    });
  return { open, lines };
}

export interface Rhythm {
  elapsedDays: number;
  spendingDays: number;
  zeroDays: number;
  peak: [string, number] | null;
  dailyAverage: number;
  activeAverage: number;
  streakDays: number;
  streakKind: "spending" | "zero";
}

/**
 * How spending is distributed across the month rather than how much of it there was. Only days
 * that have actually happened count, so a part-way month is not averaged against days still to
 * come — which would make every month look thrifty until its last day.
 */
export function rhythm(expenses: Transaction[], month: string, today: string): Rhythm {
  const [year, monthNumber] = month.split("-").map(Number);
  const daysInMonth = new Date(year, monthNumber, 0).getDate();
  const currentMonth = today.slice(0, 7);
  const elapsedDays =
    month < currentMonth ? daysInMonth : month === currentMonth ? Number(today.slice(8, 10)) : 0;

  const daily = new Map<string, number>();
  for (const transaction of expenses) {
    if (Number(transaction.date.slice(8, 10)) > elapsedDays) continue;
    const amount = Math.abs(reportableBase(transaction) ?? 0);
    const day = transaction.date.slice(0, 10);
    if (amount > 0) daily.set(day, (daily.get(day) || 0) + amount);
  }

  const spendingDays = [...daily.values()].filter((amount) => amount > 0).length;
  const peak = [...daily.entries()].sort((a, b) => b[1] - a[1])[0] || null;

  let streakDays = 0;
  let streakKind: "spending" | "zero" = "zero";
  if (elapsedDays > 0) {
    const day = (n: number) => `${month}-${String(n).padStart(2, "0")}`;
    streakKind = (daily.get(day(elapsedDays)) || 0) > 0 ? "spending" : "zero";
    for (let n = elapsedDays; n >= 1; n--) {
      if (((daily.get(day(n)) || 0) > 0) !== (streakKind === "spending")) break;
      streakDays++;
    }
  }

  const spent = [...daily.values()].reduce((sum, amount) => sum + amount, 0);
  return {
    elapsedDays,
    spendingDays,
    zeroDays: Math.max(0, elapsedDays - spendingDays),
    peak,
    dailyAverage: elapsedDays ? spent / elapsedDays : 0,
    activeAverage: spendingDays ? spent / spendingDays : 0,
    streakDays,
    streakKind,
  };
}

/** The next few recurring payments due, income excluded — this answers "what is still coming out". */
export function upcoming(data: DashboardData | null, owner: string, limit = 3) {
  return (
    data?.recurring
      .filter((r) => r.active && (owner === "All" || r.owner === owner) && r.nextDue)
      .sort((a, b) => a.nextDue.localeCompare(b.nextDue))
      .slice(0, limit) || []
  );
}
